/**
 * src/hooks/useSpatialProximity.ts
 *
 * Keeps a moving participant's view of what they can hear up to date.
 *
 * The server owns the enter/exit state machine — hysteresis, dwell and
 * cooldown all live in `spatialEncounterTracker`, because a proximity
 * trigger that starts a broadcast cannot be decided in a browser. What
 * is left for the client is the part that is genuinely a rendering
 * concern: the gain a listener hears as they walk.
 *
 * Volume is smoothed here rather than server-side. The server's number
 * is correct for the fix it was given, but consecutive fixes disagree by
 * metres, and applying each one directly makes audio breathe in and out
 * while somebody stands still. An exponential moving average with a
 * dead-band gives a level that tracks real movement and ignores noise.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  findNearbyBroadcasts,
  type NearbySpatialBroadcast,
} from '../services/spatial/spatialPresenceClient';
import { V2_API } from '../v2/config';

export interface SpatialEncounterEvent {
  type: 'spatial.participant.entered' | 'spatial.participant.exited';
  spatialObjectId: string;
  participantId: string;
  participantType: 'human' | 'ai_agent';
  distanceMeters: number | null;
  occupancy?: number;
  broadcastStarted?: boolean;
  reason?: string;
}

export interface TrackedSpatialBroadcast extends NearbySpatialBroadcast {
  encounterState: 'OUTSIDE' | 'PENDING' | 'INSIDE';
  inside: boolean;
  occupancy: number;
  /** Jitter-smoothed gain, 0..1. Prefer this over `volume` for playback. */
  smoothedVolume: number;
}

export interface UseSpatialProximityOptions {
  /** Stable id for this listener. A wallet, a session id, anything durable. */
  participantId: string | null;
  position: { lat: number; lng: number; altitudeMeters?: number | null; accuracyMeters?: number | null } | null;
  wallet?: string | null;
  participantType?: 'human' | 'ai_agent';
  /** Poll interval. Slower than the GPS, on purpose. */
  intervalMs?: number;
  /**
   * Report positions to the tracker (enabling proximity triggers) rather
   * than only reading. Off by default: waking a building is a side
   * effect and a component should have to ask for it.
   */
  track?: boolean;
  enabled?: boolean;
}

/** Weight of each new sample. Lower is smoother and slower to respond. */
const SMOOTHING_ALPHA = 0.25;
/** Gain changes below this are noise and are not applied. */
const VOLUME_DEAD_BAND = 0.02;

function smooth(previous: number | undefined, next: number): number {
  if (previous === undefined) return next;
  const blended = previous + SMOOTHING_ALPHA * (next - previous);
  return Math.abs(blended - previous) < VOLUME_DEAD_BAND ? previous : blended;
}

export function useSpatialProximity(options: UseSpatialProximityOptions): {
  broadcasts: TrackedSpatialBroadcast[];
  events: SpatialEncounterEvent[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const {
    participantId,
    position,
    wallet = null,
    participantType = 'human',
    intervalMs = 5_000,
    track = false,
    enabled = true,
  } = options;

  const [broadcasts, setBroadcasts] = useState<TrackedSpatialBroadcast[]>([]);
  const [events, setEvents] = useState<SpatialEncounterEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const volumesRef = useRef<Map<string, number>>(new Map());
  const inFlightRef = useRef(false);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const applySmoothing = useCallback((rows: any[]): TrackedSpatialBroadcast[] => {
    const volumes = volumesRef.current;
    const seen = new Set<string>();
    const out = rows.map((row) => {
      seen.add(row.spatialObjectId);
      const smoothed = smooth(volumes.get(row.spatialObjectId), Number(row.volume) || 0);
      volumes.set(row.spatialObjectId, smoothed);
      return {
        ...row,
        encounterState: row.encounterState || 'OUTSIDE',
        inside: !!row.inside,
        occupancy: Number(row.occupancy) || 0,
        smoothedVolume: Number(smoothed.toFixed(3)),
      } as TrackedSpatialBroadcast;
    });
    // Forget objects that dropped out, so returning to one later starts
    // from its real level instead of a stale average.
    for (const id of Array.from(volumes.keys())) {
      if (!seen.has(id)) volumes.delete(id);
    }
    return out;
  }, []);

  // Read as primitives so a caller that rebuilds the position object each
  // render does not restart the poll each render. Moving is what should
  // restart it, not re-rendering.
  const lat = position?.lat ?? null;
  const lng = position?.lng ?? null;
  const altitudeMeters = position?.altitudeMeters ?? null;
  const accuracyMeters = position?.accuracyMeters ?? null;

  useEffect(() => {
    if (!enabled || lat == null || lng == null) return undefined;
    if (track && !participantId) return undefined;

    let cancelled = false;

    const poll = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setLoading(true);
      try {
        if (track && participantId) {
          const resp = await fetch(`${V2_API}/spatial-broadcasts/observe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              participantId,
              participantType,
              wallet,
              lat,
              lng,
              altitudeMeters,
              accuracyMeters,
            }),
          });
          const body = await resp.json().catch(() => null);
          if (cancelled) return;
          if (!resp.ok || !body?.ok) {
            setError(body?.reason || `http_${resp.status}`);
            return;
          }
          setError(null);
          setBroadcasts(applySmoothing(body.nearby || []));
          if (Array.isArray(body.events) && body.events.length) setEvents(body.events);
          return;
        }

        const result = await findNearbyBroadcasts({
          lat,
          lng,
          altitudeMeters,
          accuracyMeters,
          participantType,
          wallet,
        });
        if (cancelled) return;
        if (!result.ok) { setError(result.reason); return; }
        setError(null);
        setBroadcasts(applySmoothing(result.broadcasts || []));
      } finally {
        inFlightRef.current = false;
        if (!cancelled) setLoading(false);
      }
    };

    void poll();
    const timer = setInterval(poll, Math.max(1_000, intervalMs));
    return () => { cancelled = true; clearInterval(timer); };
  }, [
    enabled, track, participantId, participantType, wallet, intervalMs, nonce,
    lat, lng, altitudeMeters, accuracyMeters, applySmoothing,
  ]);

  return { broadcasts, events, loading, error, refresh };
}

export default useSpatialProximity;
